import { useMemo } from 'react';

import { styled } from '@mui/material';
import { useTranslation } from '@kc-console/core';

import PageFooterButton from './PageFooterButton';
import { InitialPageFooterButtonVariantMapByEvent, PageFooterContext } from './PageFooterContext';
import { pageFooterClasses } from './theme';

import type { PageFooterContextParams, PageFooterProps } from './types';

const PageFooter = (props: PageFooterProps) => {
  const { children, buttons, loading = false, variantMap, labelMap } = props;
  const { t } = useTranslation();

  const initialLabelMap = useMemo(
    () => ({
      cancel: t('common:common.action.cancel'),
      close: t('common:common.action.close'),
      submit: t('common:common.action.apply'),
      delete: t('common:common.action.delete'),
      confirm: t('common:common.action.confirm'),
      common: t('common:common.action.confirm'),
    }),
    [t],
  );

  const contextValue = useMemo<PageFooterContextParams>(
    () => ({
      variantMap: {
        ...InitialPageFooterButtonVariantMapByEvent,
        ...variantMap,
      },
      labelMap: {
        ...initialLabelMap,
        ...labelMap,
      },
      loading,
    }),
    [initialLabelMap, labelMap, loading, variantMap],
  );

  return (
    <PageFooterContext.Provider value={contextValue}>
      <PageFooterRoot>
        <PageFooterContent>{buttons?.length ? buttons : children}</PageFooterContent>
      </PageFooterRoot>
    </PageFooterContext.Provider>
  );
};

PageFooter.Button = PageFooterButton;

export default PageFooter;

const PageFooterRoot = styled('div', {
  target: pageFooterClasses.root,
})`
  position: sticky;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.stickyActionBar};

  width: 100%;
  height: 80px;
  padding: 0 32px;

  background-color: ${({ theme }) => theme.color['surface/default']};
`;

const PageFooterContent = styled('footer', {
  target: pageFooterClasses.content,
})`
  display: flex;
  align-items: center;
  gap: 8px;

  height: 100%;
  border-top: 1px solid ${({ theme }) => theme.color['border/default']};
`;
